/**
 * The phone's bottom tab bar.
 *
 * Two of them exist and they are the same component: in the workspace it
 * switches between Karte · NavLog · Wetter · Mehr, everywhere else it is the
 * app's own navigation, because a phone has no rail and the way back to the
 * flights must not be a browser button.
 */
import { useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import { MoreSheet } from './MoreSheet';
import type { NavKey } from './Rail';
import { IconAircraft, IconFlights, IconMore, IconSettings } from './icons';

export interface TabBarItem {
  key: string;
  label: string;
  Icon: ComponentType<{ size?: number }>;
  onSelect(): void;
}

export function TabBar({ items, active }: { items: readonly TabBarItem[]; active: string }) {
  const t = useT();
  return (
    <nav className="ae-tabbar" aria-label={t('nav.aria')}>
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          aria-selected={item.key === active}
          role="tab"
          onClick={item.onSelect}
        >
          <item.Icon size={20} />
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** The tab bar the four non-workspace screens get. */
export function PageTabBar({ active }: { active?: NavKey | undefined }): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const [more, setMore] = useState(false);

  const items: TabBarItem[] = [
    { key: 'flights', label: t('nav.flights'), Icon: IconFlights, onSelect: () => navigate('/flights') },
    { key: 'aircraft', label: t('nav.aircraft'), Icon: IconAircraft, onSelect: () => navigate('/aircraft') },
    { key: 'settings', label: t('nav.settings'), Icon: IconSettings, onSelect: () => navigate('/settings') },
    { key: 'more', label: t('nav.more'), Icon: IconMore, onSelect: () => setMore(true) },
  ];

  return (
    <>
      <TabBar items={items} active={active ?? ''} />
      {more ? <MoreSheet onClose={() => setMore(false)} /> : null}
    </>
  );
}
